import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createMerchantAsset, deleteMerchantAsset } from '@/lib/api/merchant-assets';
import { getService, updateService } from '@/lib/api/services';
import { serviceKeys } from '@/hooks/use-services';
import { merchantAssetKeys } from '@/hooks/use-merchant-assets';
import type { MerchantAsset } from '@/types/merchant-asset';
import type { ServiceTheme } from '@/types/service';

/** Input for a single "Replace file" run. */
export interface ReplaceAssetVars {
  /** The asset being replaced — carries `usedBy`, `usageUnavailable`, `type`, `name`. */
  asset: MerchantAsset;
  /** New CDN file URL (uploaded or pasted in the dialog) — becomes the new asset's file. */
  newFileUrl: string;
}

/**
 * Structured result of the replace flow — the UI derives its message from this so
 * the feedback is consistent across every path (Spec §Ergebnis-Kontrakt).
 */
export interface ReplaceAssetResult {
  newAssetId: string;
  newAssetName: string;
  /** Services successfully repointed to the new asset. */
  relinkedServiceIds: string[];
  /** Services whose relink failed — the old asset stays linked for these. */
  failedServiceIds: string[];
  /** The old asset was soft-deleted (only when every relink succeeded). */
  oldAssetRetired: boolean;
  /** Soft-delete was attempted but failed (non-fatal). */
  retireFailed?: boolean;
  /** Degraded: usage could not be derived → no relink/retire attempted. */
  usageUnavailable?: boolean;
}

/** The three theme slots that can hold a MerchantAsset reference (logo has none). */
const ASSET_SLOTS = ['stylesheetAssetId', 'headerAssetId', 'backgroundAssetId'] as const;

/**
 * "Replace file" orchestration (Issue #417). The Facade has no in-place file swap:
 * a `currentFileUrl` PUT retires the asset to `status=replaced` (terminal, vanishes
 * from the library). The only safe write is CREATE, so this hook replaces the unsafe
 * swap with: create a new active asset → best-effort relink every referencing service
 * onto it → soft-delete the old asset (only when nothing references it any more).
 *
 * Ordering is the safety: the new asset is created first (old untouched); a relink
 * failure leaves the old asset active and linked so every service keeps rendering;
 * the old asset is retired only when no service points at it. Worst case is a
 * duplicate, never a broken service.
 *
 * The Facade replaces the WHOLE theme object on update (no deep merge), so each
 * relink loads the service's full theme and swaps only the matching asset-reference
 * field(s), writing everything else back verbatim.
 */
export function useReplaceAsset(merchantId: string) {
  const queryClient = useQueryClient();

  return useMutation<ReplaceAssetResult, Error, ReplaceAssetVars>({
    mutationFn: async ({ asset, newFileUrl }) => {
      const oldAssetId = asset.assetId;

      // 1. Create the new active asset first — the old one stays untouched.
      //    Same name (Owner decision: continuity) and type.
      const created = await createMerchantAsset({
        merchantId,
        name: asset.name,
        type: asset.type,
        currentFileUrl: newFileUrl,
      });
      const newAssetId = created.assetId;

      // 2. Degraded path: usage couldn't be derived → don't relink, don't retire.
      //    The user is told a second same-named asset now exists (manual relink).
      if (asset.usageUnavailable) {
        return {
          newAssetId,
          newAssetName: created.name,
          relinkedServiceIds: [],
          failedServiceIds: [],
          oldAssetRetired: false,
          usageUnavailable: true,
        };
      }

      // 3. Best-effort relink of every referencing service. Continue past an
      //    individual failure (no fail-fast) to maximise migration; collect results.
      const relinkedServiceIds: string[] = [];
      const failedServiceIds: string[] = [];
      for (const usage of asset.usedBy) {
        try {
          const svc = await getService(usage.serviceId, merchantId);
          const theme = swapAssetReference(svc.theme, oldAssetId, newAssetId);
          await updateService(usage.serviceId, { theme });
          relinkedServiceIds.push(usage.serviceId);
        } catch {
          failedServiceIds.push(usage.serviceId);
        }
      }

      // 4. Retire the old asset only if EVERY relink succeeded (nothing points at it
      //    any more). Best-effort: a failed soft-delete leaves an orphan, not a
      //    broken service — the run still counts as success.
      let oldAssetRetired = false;
      let retireFailed = false;
      if (failedServiceIds.length === 0) {
        try {
          await deleteMerchantAsset(oldAssetId, merchantId);
          oldAssetRetired = true;
        } catch {
          retireFailed = true;
        }
      }

      return {
        newAssetId,
        newAssetName: created.name,
        relinkedServiceIds,
        failedServiceIds,
        oldAssetRetired,
        ...(retireFailed ? { retireFailed: true } : {}),
      };
    },
    onSuccess: () => {
      // Refresh both axes: the asset library (old gone / new present) and the
      // services (relinked theme references).
      queryClient.invalidateQueries({ queryKey: merchantAssetKeys.lists(merchantId) });
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
    },
  });
}

/**
 * Return a copy of `theme` with every asset-reference slot that points at
 * `oldAssetId` repointed to `newAssetId`. A service can carry the same asset in
 * more than one slot — all matching slots are swapped. Everything else is preserved
 * verbatim (theme update is a full replacement).
 */
export function swapAssetReference(
  theme: ServiceTheme | undefined,
  oldAssetId: string,
  newAssetId: string,
): ServiceTheme {
  const next: ServiceTheme = { ...(theme ?? {}) };
  for (const slot of ASSET_SLOTS) {
    if (next[slot] === oldAssetId) {
      next[slot] = newAssetId;
    }
  }
  return next;
}
